/**
 * Recurring-event expansion — pure logic extracted from EventListModule's
 * `expandedEvents` useMemo (PR3). The orchestrator keeps the `useMemo`
 * wrapper; this module owns the deterministic, testable core.
 *
 * The −3mo/+12mo window is derived from an injected `now` so the expansion is
 * deterministic under test. Behavior (occurrence counts, exception override by
 * `recurringEventId`+`originalStartAt`, cancelled/archived skip, virtual-instance
 * markers, 1h default duration, invalid-RRULE fallback) is byte-faithful to the
 * original memo. Diagnostic `console.*` calls are gated behind `DEBUG_RECURRENCE`
 * so they no longer fire on every expansion in production (flip to `true` locally
 * to re-enable the verbose trace).
 */
import { RRule } from 'rrule';
import { parseEventDate, isDateOnlyString } from './dateTimeUtils';
import { occToken, occInstanceToken } from '@/lib/agent-effects/occToken';
import type { CalendarTimeFilter } from './eventFetchWindow';

// Re-export the canonical occurrence-token helpers (defined in the `lib` leaf so
// the agent-effects ingest path can reuse them without a modules→app import). The
// recurrence expander + the calendar chips key occurrences through these.
export { occToken, occInstanceToken };

/**
 * Padding (in days) applied around the navigated view when anchoring the
 * recurrence-expansion window to it. A small pad guarantees an occurrence that
 * begins just before the visible range (multi-day spans, week/month edge cells)
 * still expands and renders.
 */
const VIEW_PAD_DAYS = 14;

/** Toggle to re-enable the verbose expansion trace during local debugging. */
const DEBUG_RECURRENCE = false;

/** Number of ms in one whole day. */
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Format a Date's LOCAL year/month/day as a date-only `YYYY-MM-DD` string.
 * Pure calendar helper used only by `addDaysDateOnly`, which constructs a fresh
 * LOCAL Date from Y/M/D and advances it with `setDate` (calendar-aware, no DST
 * instant carried), so reading the LOCAL Y/M/D back is exact.
 *
 * It must NOT be used to derive an all-day RECURRENCE occurrence's date string:
 * rrule iterates on dtstart's UTC fields, pinning every occurrence to a fixed
 * UTC instant, so the LOCAL calendar day only matches while the local UTC offset
 * equals dtstart's (i.e. within one DST period). See `formatDateOnlyUTC`.
 */
function formatDateOnlyLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Format a Date's UTC year/month/day as a date-only `YYYY-MM-DD` string.
 *
 * This is the DST-INVARIANT way to derive an all-day recurrence occurrence's
 * calendar day. The all-day date-only path parses dtstart as UTC midnight (see
 * the expansion loop), so each rrule occurrence sits at exactly `00:00:00Z` on
 * its true calendar day regardless of any DST transition inside the expansion
 * window. Reading the UTC Y/M/D recovers that day; reading the LOCAL Y/M/D would
 * shift every post-transition occurrence one day early (the #4493 follow-up bug).
 */
function formatDateOnlyUTC(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Parse a date-only `YYYY-MM-DD` string as UTC midnight, for use as the all-day
 * recurrence dtstart. Using UTC midnight (rather than the shared LOCAL-midnight
 * `parseEventDate`) keeps the occurrence instants at `00:00:00Z`, so deriving
 * each occurrence's day via `formatDateOnlyUTC` is DST-invariant.
 */
function parseDateOnlyUTC(dateOnly: string): Date {
  const [y, m, d] = dateOnly.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
}

/**
 * The agent-effect `instanceId` for an event chip, or `undefined` when the chip
 * is not a recurring occurrence. Covers BOTH a master's expanded VIRTUAL
 * occurrence (`_isVirtual` + `_originalStartAt`) and a DETACHED single-occurrence
 * exception (`recurringEventId` + `originalStartAt`), so every occurrence chip is
 * keyed by its occurrence identity. A plain non-recurring event ⇒ `undefined`
 * (keyed by id alone). Pairs with {@link occInstanceToken} on the receipt side.
 */
export function eventOccInstanceId(event: {
  _isVirtual?: boolean;
  _originalStartAt?: string;
  recurringEventId?: string | null;
  originalStartAt?: string | null;
} | null | undefined): string | undefined {
  if (!event) return undefined;
  const raw = event._isVirtual
    ? event._originalStartAt
    : (event.recurringEventId ? event.originalStartAt : undefined);
  return occInstanceToken(raw);
}

/** Read an event field from the top level or its nested `attributes` (mirrors the backend `field()`). */
function eventField(ev: any, name: string): unknown {
  return ev?.[name] ?? ev?.attributes?.[name];
}

/**
 * Every identity value a single-occurrence exception may reference its master by.
 * An exception's `recurringEventId` is the master's LOCAL object id for a Xavior-
 * native series, but the PROVIDER's recurring-event id for a Google-synced one —
 * so the suppression match must probe ALL of them: `id` AND any
 * `providerEventId`/`googleEventId`/`externalId` the master row carries. Mirrors
 * the backend `masterKeys()` in apps/api `event-expansion.ts`.
 */
export function masterIdentityKeys(master: any): string[] {
  const keys: unknown[] = [
    master?.id,
    eventField(master, 'providerEventId'),
    eventField(master, 'googleEventId'),
  ];
  const ext = eventField(master, 'externalId');
  if (typeof ext === 'string' && ext.startsWith('calendar:')) keys.push(ext.slice('calendar:'.length));
  return keys.filter((k): k is string => typeof k === 'string' && k.length > 0);
}

/** Add a whole number of days to a date-only `YYYY-MM-DD` string, returning a
 *  date-only string. Pure calendar arithmetic (no timezone involved). */
function addDaysDateOnly(dateOnly: string, days: number): string {
  const [y, m, d] = dateOnly.split('-').map(Number);
  const dt = new Date(y, m - 1, d, 0, 0, 0, 0);
  dt.setDate(dt.getDate() + days);
  return formatDateOnlyLocal(dt);
}

export interface ExpandRecurringRange {
  rangeStart: Date;
  rangeEnd: Date;
}

/**
 * Compute the default expansion window: 3 months back, 12 months forward from
 * `now`, matching the original `expandedEvents` memo (rangeStart at local
 * midnight, rangeEnd at end-of-day).
 */
export function computeRecurrenceRange(now: Date): ExpandRecurringRange {
  const rangeStart = new Date(now);
  rangeStart.setMonth(rangeStart.getMonth() - 3);
  rangeStart.setHours(0, 0, 0, 0);
  const rangeEnd = new Date(now);
  rangeEnd.setMonth(rangeEnd.getMonth() + 12);
  rangeEnd.setHours(23, 59, 59, 999);
  return { rangeStart, rangeEnd };
}

/**
 * Compute the bounds of the calendar view the user has navigated to, padded by
 * {@link VIEW_PAD_DAYS} on each side. Mirrors the day/week/month math the server
 * fetch window is keyed on ({@link computeEventFetchWindow}) so the expander
 * covers exactly the days the server returned masters for.
 */
function computeViewedBounds(date: Date, filter: CalendarTimeFilter): ExpandRecurringRange {
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();

  let viewStart: Date;
  let viewEnd: Date;
  if (filter === 'today') {
    viewStart = new Date(y, m, d, 0, 0, 0, 0);
    viewEnd = new Date(y, m, d, 23, 59, 59, 999);
  } else if (filter === 'week') {
    const dayOfWeek = date.getDay(); // 0 = Sunday
    viewStart = new Date(y, m, d - dayOfWeek, 0, 0, 0, 0);
    viewEnd = new Date(y, m, d - dayOfWeek + 6, 23, 59, 59, 999);
  } else {
    // month: ±7 days to match the adjacent-month grid cells the view paints.
    viewStart = new Date(new Date(y, m, 1, 0, 0, 0, 0).getTime() - 7 * MS_PER_DAY);
    viewEnd = new Date(new Date(y, m + 1, 0, 23, 59, 59, 999).getTime() + 7 * MS_PER_DAY);
  }

  const rangeStart = new Date(viewStart.getTime() - VIEW_PAD_DAYS * MS_PER_DAY);
  rangeStart.setHours(0, 0, 0, 0);
  const rangeEnd = new Date(viewEnd.getTime() + VIEW_PAD_DAYS * MS_PER_DAY);
  rangeEnd.setHours(23, 59, 59, 999);
  return { rangeStart, rangeEnd };
}

/**
 * Compute the recurrence-expansion window ANCHORED to the navigated view, while
 * still covering the default now-relative window (issue #4298).
 *
 * The default {@link computeRecurrenceRange} window (now −3mo / +12mo) is unioned
 * with the padded bounds of the currently-viewed period, so:
 *   - the current-window render is byte-identical to before (the default window
 *     always dominates when the view is near today), and
 *   - navigating the calendar >12 months out (or >3 months back) still generates
 *     occurrences for the dates on screen — they no longer "stop" after ~1 year.
 *
 * Bounded by construction: the window is min/max of two finite ranges (never
 * unbounded), so a far-future view expands a wide-but-finite span, not the whole
 * series.
 *
 * When `calendarDate`/`timeFilter` are absent the result is exactly the default
 * window, so callers that don't thread the view keep prior behavior.
 */
export function computeViewAnchoredRecurrenceRange(
  now: Date,
  calendarDate?: Date | null,
  timeFilter?: CalendarTimeFilter | null,
): ExpandRecurringRange {
  const base = computeRecurrenceRange(now);
  if (!calendarDate || !timeFilter) return base;

  const viewed = computeViewedBounds(calendarDate, timeFilter);
  return {
    rangeStart: new Date(Math.min(base.rangeStart.getTime(), viewed.rangeStart.getTime())),
    rangeEnd: new Date(Math.max(base.rangeEnd.getTime(), viewed.rangeEnd.getTime())),
  };
}

/**
 * Date parsing is unified onto the canonical `dateTimeUtils.parseEventDate`
 * (imported above): date-only `YYYY-MM-DD` parses as LOCAL midnight, ISO
 * datetimes parse normally. recurrence.ts previously kept a divergent LOCAL copy
 * that parsed date-only as UTC midnight; that disagreement re-emitted every
 * all-day occurrence as a full UTC-ISO datetime, which a LOCAL-midnight renderer
 * in a negative-offset zone shifted to the prior day and printed as a bogus time
 * ("5:00–5:00 PM") — issue #4215. Keeping date-only masters date-only (see the
 * expansion loop below) makes the all-day chip survive end-to-end with no
 * off-by-one. The original #3490 phantom-ghost concern is covered by the
 * date-only round-trip + occurrence-count tests.
 *
 * #4215 follow-up (#4493/#4504): only TIMED masters parse their start via the
 * shared LOCAL `parseEventDate` (or the fake-UTC/startLocal trick). ALL-DAY
 * masters parse dtstart as UTC midnight (`parseDateOnlyUTC`) and read each
 * occurrence's day off the UTC fields (`formatDateOnlyUTC`), which is DST-
 * invariant — a LOCAL read would shift every post-transition occurrence a day
 * early. The all-day `rule.between` window is likewise UTC-day-aligned so the
 * leading/trailing edge day is not skewed out by the LOCAL-midnight range.
 */

/**
 * Expand recurring master events into virtual occurrences within the given
 * range, applying exception overrides and skipping cancelled/archived ones.
 *
 * Byte-faithful port of the EventListModule `expandedEvents` memo body
 * (only the window is parameterized for determinism).
 */
export function expandRecurringEvents(
  events: any[],
  { rangeStart, rangeEnd }: ExpandRecurringRange,
): any[] {
  const masters = events.filter((e: any) => e.rrule && e.rrule.length > 0 && !e.recurringEventId);
  const exceptions = events.filter((e: any) => e.recurringEventId);
  const oneOff = events.filter((e: any) => (!e.rrule || e.rrule.length === 0) && !e.recurringEventId);

  if (DEBUG_RECURRENCE) {
    console.log('[EventList] Recurring expansion:', { masters: masters.length, exceptions: exceptions.length, oneOff: oneOff.length, totalEvents: events.length });
    if (masters.length > 0) {
      console.log('[EventList] Master events:', masters.map((m: any) => ({ id: m.id, title: m.title, rrule: m.rrule, startAt: m.startAt })));
    } else if (events.length > 0) {
      // Log all event fields for the first event to debug whether rrule is present
      const sample = events[0] as any;
      console.log('[EventList] No masters found. Sample event fields:', Object.keys(sample));
      console.log('[EventList] Sample event rrule value:', sample.rrule, 'type:', typeof sample.rrule);
    }
  }

  const expanded: any[] = [...oneOff];

  for (const master of masters) {
    try {
      // RRule operates entirely in UTC. A 6 PM PDT event has startAt=01:00Z Thursday,
      // so passing it as dtstart makes BYDAY=WE anchor on Thursday → occurrences land
      // on Tuesday in PDT. Fix: parse startLocal as if it were UTC ("fake-UTC" trick)
      // so the weekday RRule sees matches the local calendar day. Then shift each
      // fake-UTC occurrence back to real UTC by adding the constant offset between
      // the fake dtstart and the real startAt.
      let masterStart: Date | null = null;
      let occurrenceOffset = 0; // ms to add to fake-UTC occurrence → real UTC

      // #4215 follow-up (#4493): a date-only (all-day) master is DST-invariant.
      // Parse its dtstart as UTC midnight so every rrule occurrence sits at
      // 00:00:00Z on its true calendar day; the occurrence day is then read off
      // the UTC fields (formatDateOnlyUTC) below. The fake-UTC/startLocal trick is
      // only for TIMED masters whose UTC start crosses a midnight boundary.
      const allDayMaster = isDateOnlyString(master.startAt);

      if (allDayMaster) {
        masterStart = parseDateOnlyUTC(master.startAt);
      } else {
        const localStr: string | undefined = master.startLocal;
        if (localStr && typeof localStr === 'string') {
          const m = localStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
          if (m) {
            const fakeUtc = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] ?? 0)));
            masterStart = fakeUtc;
            const realStart = parseEventDate(master.startAt);
            if (realStart) occurrenceOffset = realStart.getTime() - fakeUtc.getTime();
          }
        }
        if (!masterStart) masterStart = parseEventDate(master.startAt);
      }

      if (!masterStart) {
        if (DEBUG_RECURRENCE) console.warn('[EventList] Could not parse startAt for master', master.id, master.startAt);
        expanded.push(master);
        continue;
      }
      const parsed = RRule.parseString(master.rrule);
      if (DEBUG_RECURRENCE) console.log('[EventList] Parsed RRULE for', master.title, ':', JSON.stringify(parsed));
      const rule = new RRule({
        ...parsed,
        dtstart: masterStart,
      });
      // All-day occurrences sit at UTC midnight (dtstart is UTC midnight), but
      // rangeStart/rangeEnd are built at LOCAL midnight (computeRecurrenceRange).
      // In a non-UTC zone the occurrence on rangeStart's calendar day is offset
      // from rangeStart by the local UTC offset, so a LOCAL-bounded `between` would
      // skew the edge day in/out (drop the leading day in a negative-offset zone).
      // Query the all-day window on the SAME UTC-day frame the occurrences live in:
      // UTC midnight of rangeStart's local day → UTC end-of-day of rangeEnd's local
      // day. Timed masters keep the exact LOCAL bounds (their occurrences are real
      // instants, not date-only days). See `parseDateOnlyUTC`/`formatDateOnlyUTC`.
      const [qStart, qEnd] = allDayMaster
        ? [
            new Date(Date.UTC(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate(), 0, 0, 0, 0)),
            new Date(Date.UTC(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate(), 23, 59, 59, 999)),
          ]
        : [rangeStart, rangeEnd];
      const occurrences = rule.between(qStart, qEnd, true);
      if (DEBUG_RECURRENCE) console.log('[EventList] Expanded', master.title, ':', occurrences.length, 'occurrences between', rangeStart.toISOString(), 'and', rangeEnd.toISOString());

      // #4215: a date-only (all-day) master must expand into date-only
      // occurrences so the all-day chip stays date-only end-to-end (no UTC-ISO
      // datetime that re-parses a day early / prints a bogus time). For these,
      // the duration is a WHOLE-DAY span and the exception-match key is date-only
      // (Google stores all-day exception originalStartAt as YYYY-MM-DD too).
      // (`allDayMaster` is computed above, before the dtstart parse.)
      const spanDays = allDayMaster && isDateOnlyString(master.endAt)
        ? Math.max(0, Math.round((new Date(master.endAt + 'T00:00:00').getTime()
            - new Date(master.startAt + 'T00:00:00').getTime()) / MS_PER_DAY))
        : 0;

      // Duration from real UTC start/end, not from the fake dtstart (which may be
      // shifted by occurrenceOffset for timezone-crossing events).
      const realStartMs = parseEventDate(master.startAt)?.getTime() ?? masterStart.getTime();
      const duration = master.endAt
        ? new Date(master.endAt).getTime() - realStartMs
        : 60 * 60 * 1000; // default 1h

      // Pre-index this series' single-occurrence EXCEPTIONS by a normalized
      // occurrence token so a moved/cancelled exception matches its generated
      // occurrence even when the two ISO strings differ only in format/offset:
      // the stored `originalStartAt` is often local-offset form
      // (`2026-06-29T09:00:00-07:00`) while the RRULE occurrence is the UTC-Z
      // instant (`2026-06-29T16:00:00.000Z`) — the SAME moment, different strings.
      // The old exact-string `ex.originalStartAt === occKey` compare silently
      // failed on that skew, so the moved exception never rendered AND the original
      // occurrence was not suppressed. `occToken` collapses both sides to one token
      // (epoch-ms for timed, calendar-date for all-day). We key by EVERY identity an
      // exception may reference the master by (`masterIdentityKeys`: local id for
      // native series, provider/google/external id for a synced one) so the
      // `master.id` lookup below resolves whichever id the writer stamped on
      // `recurringEventId`. Mirrors the backend suppression set in apps/api
      // event-expansion.ts. (#calendar occurrence-exception render)
      //
      // KNOWN LIMITATION (out of scope — documented, NOT fixed here): an occurrence
      // moved to a date OUTSIDE the calendar's fetch window is dropped by the DB
      // range query, which filters on the exception's NEW `startAt`, not its
      // `originalStartAt` (see
      // packages/persistence/src/postgres/repos/object-event-range-sql.ts). So a
      // cross-window move never delivers its exception to this expander, the
      // original slot is not suppressed, and the original occurrence still renders.
      // Tracked in the changeset follow-up.
      const identityKeys = masterIdentityKeys(master);
      const overrideByKey = new Map<string, any>();
      for (const ex of exceptions) {
        // Only THIS master's exceptions — `recurringEventId` must reference one of
        // the master's identities. Guards against a same-slot token from a
        // different series wrongly suppressing this occurrence.
        if (!identityKeys.includes(ex.recurringEventId)) continue;
        const exTok = occToken(ex.originalStartAt, allDayMaster);
        if (!exTok) continue;
        for (const k of identityKeys) overrideByKey.set(`${k} ${exTok}`, ex);
      }

      for (const occ of occurrences) {
        // Convert fake-UTC occurrence back to real UTC before producing the ISO string.
        // For all-day masters occurrenceOffset is 0 (no startLocal), so this is a no-op.
        const occRealMs = occ.getTime() + occurrenceOffset;
        // Match key: date-only for all-day masters (matches Google's stored format),
        // real-UTC ISO for timed masters (fake-UTC corrected). For all-day masters
        // the day is read off the occurrence's UTC fields so it stays correct on
        // both sides of a DST transition (#4493 follow-up; dtstart is UTC midnight).
        const occKey = allDayMaster ? formatDateOnlyUTC(occ) : new Date(occRealMs).toISOString();

        // Exception match via the NORMALIZED token (replaces the brittle exact-string
        // `ex.originalStartAt === occKey`). The occurrence instant is the rrule occ
        // (UTC-midnight) for all-day masters, else the fake-UTC-corrected real
        // instant. Look up by `master.id` (always present in `identityKeys`).
        const occTok = occToken(allDayMaster ? occ : new Date(occRealMs), allDayMaster);
        const exception = occTok ? overrideByKey.get(`${master.id} ${occTok}`) : undefined;
        if (exception) {
          if (!exception.cancelledOccurrence && !exception.archived) expanded.push(exception);
          // matched cancelled/archived exception = deleted occurrence → suppress, push nothing
        } else if (allDayMaster) {
          // DST-invariant: derive the occurrence day from UTC fields (dtstart is
          // UTC midnight), so post-transition occurrences keep their true day.
          const occStart = formatDateOnlyUTC(occ);
          expanded.push({
            ...master,
            _isVirtual: true,
            _masterEventId: master.id,
            _originalStartAt: occStart,
            startAt: occStart,
            endAt: master.endAt ? addDaysDateOnly(occStart, spanDays) : occStart,
          });
        } else {
          expanded.push({
            ...master,
            _isVirtual: true,
            _masterEventId: master.id,
            _originalStartAt: occKey,
            startAt: occKey,
            endAt: new Date(occRealMs + duration).toISOString(),
          });
        }
      }
    } catch (err) {
      if (DEBUG_RECURRENCE) console.warn('[EventList] Failed to expand RRULE for event', master.id, err);
      expanded.push(master); // fallback: show master as-is
    }
  }

  return expanded;
}
